import React, { useState, useEffect, useCallback } from 'react';
import {
  getServers,
  createServer,
  updateServer,
  deleteServer,
  getAppsByServer,
  createApp,
  updateApp,
  deleteApp,
  triggerBackup,
  triggerDeploy,
  getLastBackup,
  getBackupHistory,
} from '../services/api';
import ServerModal from '../components/ServerModal';
import AppModal from '../components/AppModal';
import ScheduleModal from '../components/ScheduleModal';
import DeployModal from '../components/DeployModal';
import { useNotification } from '../context/NotificationContext';
import {
  FiPlus, FiChevronDown, FiChevronRight, FiEdit, FiTrash2,
  FiDatabase, FiServer, FiClock, FiCode, FiX, FiHardDrive,
  FiCheckCircle, FiAlertCircle, FiLoader
} from 'react-icons/fi';
import '../styles/ServersPage.css';

// ─── Helpers ────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

// ─── Backup History Drawer ───────────────────────────────────────────────────

function BackupHistoryDrawer({ target, targetType, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getBackupHistory(targetType, target._id)
      .then(res => setHistory(res.data?.data || []))
      .catch(() => setError('Failed to load backup history'))
      .finally(() => setLoading(false));
  }, [target._id, targetType]);

  return (
    <>
      {/* Backdrop */}
      <div className="bh-backdrop" onClick={onClose} />

      {/* Drawer */}
      <div className="bh-drawer">
        <div className="bh-drawer-header">
          <div>
            <p className="bh-drawer-label">Backup History</p>
            <h3 className="bh-drawer-title">{target.name}</h3>
          </div>
          <button className="bh-close-btn" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        <div className="bh-drawer-body">
          {loading && (
            <div className="bh-state">
              <FiLoader className="bh-spin" size={24} />
              <p>Loading history…</p>
            </div>
          )}

          {error && (
            <div className="bh-state bh-state--error">
              <FiAlertCircle size={24} />
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && history.length === 0 && (
            <div className="bh-state">
              <FiDatabase size={32} opacity={0.3} />
              <p>No backups yet</p>
            </div>
          )}

          {!loading && !error && history.length > 0 && (
            <div className="bh-timeline">
              {history.map((entry, idx) => (
                <div key={entry.job_id} className={`bh-entry bh-entry--${entry.status}`}>
                  <div className="bh-entry-icon">
                    {entry.status === 'completed'
                      ? <FiCheckCircle size={16} />
                      : <FiAlertCircle size={16} />}
                  </div>
                  <div className="bh-entry-body">
                    <div className="bh-entry-top">
                      <span className="bh-entry-date">{formatDate(entry.backed_up_at || entry.completed_at)}</span>
                      <span className={`bh-badge bh-badge--${entry.status}`}>{entry.status}</span>
                    </div>

                    {entry.status === 'completed' && (
                      <div className="bh-entry-stats">
                        <span title="Data added this run">
                          <FiHardDrive size={12} /> +{entry.added_human}
                        </span>
                        <span className="bh-dot" />
                        <span title="Total repo size on S3">
                          Total {entry.total_human}
                        </span>
                        {entry.snapshot_id && (
                          <>
                            <span className="bh-dot" />
                            <span className="bh-snapshot" title="Snapshot ID">
                              #{entry.snapshot_id.slice(0, 8)}
                            </span>
                          </>
                        )}
                      </div>
                    )}

                    {entry.status === 'failed' && entry.error && (
                      <p className="bh-entry-error">{entry.error}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Last Backup Badge ───────────────────────────────────────────────────────

function LastBackupBadge({ targetType, targetId }) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    getLastBackup(targetType, targetId)
      .then(res => setInfo(res.data?.data || null))
      .catch(() => { });
  }, [targetType, targetId]);

  if (!info) return null;

  return (
    <span className="last-backup-badge" title={`Snapshot: ${info.snapshot_id || 'N/A'}`}>
      <FiDatabase size={11} />
      <span>{info.total_human}</span>
      <span className="lbb-sep">·</span>
      <span>{timeAgo(info.backed_up_at || info.completed_at)}</span>
    </span>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

function ServersPage() {
  const [servers, setServers] = useState([]);
  const [expandedServers, setExpandedServers] = useState(new Set());
  const [serverApps, setServerApps] = useState({});
  const [loading, setLoading] = useState(true);

  const [showServerModal, setShowServerModal] = useState(false);
  const [showAppModal, setShowAppModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);

  const [editingServer, setEditingServer] = useState(null);
  const [editingApp, setEditingApp] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [deployTarget, setDeployTarget] = useState(null);

  // History drawer state
  const [historyTarget, setHistoryTarget] = useState(null); // { target, targetType }

  const { notify, notifyJobStart } = useNotification();

  const loadServers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getServers();
      setServers(response.data.data || []);
    } catch (error) {
      console.error('Failed to load servers:', error);
      notify('Failed to load servers', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { loadServers(); }, [loadServers]);

  const loadAppsForServer = async (serverId) => {
    try {
      const response = await getAppsByServer(serverId);
      setServerApps(prev => ({ ...prev, [serverId]: response.data.data || [] }));
    } catch (error) {
      console.error('Failed to load apps:', error);
    }
  };

  const toggleServer = async (serverId) => {
    const newExpanded = new Set(expandedServers);
    if (newExpanded.has(serverId)) {
      newExpanded.delete(serverId);
    } else {
      newExpanded.add(serverId);
      if (!serverApps[serverId]) await loadAppsForServer(serverId);
    }
    setExpandedServers(newExpanded);
  };

  const handleCreateServer = () => { setEditingServer(null); setShowServerModal(true); };
  const handleEditServer = (server) => { setEditingServer(server); setShowServerModal(true); };

  const handleDeleteServer = async (serverId) => {
    if (!window.confirm('Are you sure you want to delete this server?')) return;
    try {
      await deleteServer(serverId);
      await loadServers();
    } catch (error) {
      console.error('Failed to delete server:', error);
      notify('Failed to delete server', 'error');
    }
  };

  const handleSaveServer = async (serverData) => {
    try {
      if (editingServer) { await updateServer(editingServer._id, serverData); }
      else { await createServer(serverData); }
      setShowServerModal(false);
      await loadServers();
    } catch (error) {
      console.error('Failed to save server:', error);
      notify('Failed to save server', 'error');
    }
  };

  const handleCreateApp = (serverId) => { setEditingApp({ server_id: serverId }); setShowAppModal(true); };
  const handleEditApp = (app) => { setEditingApp(app); setShowAppModal(true); };

  const handleDeleteApp = async (appId, serverId) => {
    if (!window.confirm('Are you sure you want to delete this app?')) return;
    try {
      await deleteApp(appId);
      await loadAppsForServer(serverId);
    } catch (error) {
      console.error('Failed to delete app:', error);
      notify('Failed to delete app', 'error');
    }
  };

  const handleSaveApp = async (appData) => {
    try {
      if (editingApp && editingApp._id) { await updateApp(editingApp._id, appData); }
      else { await createApp(appData); }
      setShowAppModal(false);
      await loadAppsForServer(appData.server_id);
    } catch (error) {
      console.error('Failed to save app:', error);
      notify('Failed to save app', 'error');
    }
  };

  const handleBackupServer = async (server) => {
    try {
      const response = await triggerBackup('server', server._id);
      const jobId = response.data?.jobId || response.data?.data?.jobId || response.data?.data?._id;
      notifyJobStart(jobId, `Backup: ${server.name}`);
    } catch (error) {
      console.error('Failed to start backup:', error);
      notify('Failed to start server backup', 'error');
    }
  };

  const handleBackupApp = async (app) => {
    try {
      const response = await triggerBackup('app', app._id);
      const jobId = response.data?.jobId || response.data?.data?.jobId || response.data?.data?._id;
      notifyJobStart(jobId, `Backup: ${app.name}`);
    } catch (error) {
      console.error('Failed to start backup:', error);
      notify('Failed to start app backup', 'error');
    }
  };

  const handleSchedule = (type, target) => { setScheduleTarget({ type, target }); setShowScheduleModal(true); };

  const handleSaveSchedule = async (scheduleData) => {
    try {
      const { type, target } = scheduleTarget;
      if (type === 'server') { await updateServer(target._id, scheduleData); await loadServers(); }
      else { await updateApp(target._id, scheduleData); await loadAppsForServer(target.server_id); }
      setShowScheduleModal(false);
    } catch (error) {
      console.error('Failed to save schedule:', error);
      notify('Failed to save schedule', 'error');
    }
  };

  const handleDeploy = (type, target) => { setDeployTarget({ type, target }); setShowDeployModal(true); };

  const handleDeploySubmit = async (deployData) => {
    try {
      const { type, target } = deployTarget;
      const response = await triggerDeploy(type, target._id, deployData.new_server_ip, deployData.backup_to_restore);
      const jobId = response.data?.jobId || response.data?.data?.jobId || response.data?.data?._id;
      setShowDeployModal(false);
      notifyJobStart(jobId, `Deploy: ${target.name}`);
    } catch (error) {
      console.error('Failed to start deployment:', error);
      notify('Failed to start deployment', 'error');
    }
  };

  const openHistory = (e, targetType, target) => {
    e.stopPropagation();
    setHistoryTarget({ targetType, target });
  };

  if (loading) return <div className="loading">Loading servers...</div>;

  return (
    <div className="servers-page">
      <div className="page-header">
        <h2>Servers</h2>
        <button className="btn btn-primary" onClick={handleCreateServer}>
          <FiPlus /> Add Server
        </button>
      </div>

      <div className="servers-list">
        {servers.length === 0 ? (
          <div className="empty-state">
            <p>No servers configured yet.</p>
            <button className="btn btn-primary" onClick={handleCreateServer}>
              <FiPlus /> Add Your First Server
            </button>
          </div>
        ) : (
          servers.map(server => (
            <div key={server._id} className="server-card">
              <div className="server-header" onClick={() => toggleServer(server._id)}>
                <div className="server-info">
                  {expandedServers.has(server._id) ? <FiChevronDown /> : <FiChevronRight />}
                  <FiServer className="server-icon" />
                  <div>
                    <h3>{server.name}</h3>
                    <div className="server-meta-row">
                      <span className="server-ip">{server.ip}</span>
                      {server.os_version && <span className="server-meta"> • {server.os_version}</span>}
                      {server.node_version && <span className="server-meta"> • Node {server.node_version}</span>}
                      {/* Last backup badge — loads independently */}
                      <LastBackupBadge targetType="server" targetId={server._id} />
                    </div>
                  </div>
                </div>

                <div className="server-actions" onClick={e => e.stopPropagation()}>
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={e => openHistory(e, 'server', server)}
                    title="Backup History"
                  >
                    <FiClock size={14} /> History
                  </button>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => handleBackupServer(server)}
                    title="Backup Server"
                  >
                    <FiDatabase /> Backup
                  </button>
                  <button
                    className="btn btn-sm btn-info"
                    onClick={() => handleDeploy('server', server)}
                    title="Deploy"
                  >
                    <FiServer /> Deploy
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleSchedule('server', server)}
                    title="Schedule"
                  >
                    <FiClock /> Schedule
                  </button>
                  <button className="btn btn-sm" onClick={() => handleEditServer(server)} title="Edit">
                    <FiEdit />
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDeleteServer(server._id)} title="Delete">
                    <FiTrash2 />
                  </button>
                </div>
              </div>

              {expandedServers.has(server._id) && (
                <div className="apps-section">
                  <div className="apps-header">
                    <h4>Applications</h4>
                    <button className="btn btn-sm btn-primary" onClick={() => handleCreateApp(server._id)}>
                      <FiPlus /> Add App
                    </button>
                  </div>

                  {serverApps[server._id]?.length > 0 ? (
                    <div className="apps-list">
                      {serverApps[server._id].map(app => (
                        <div key={app._id} className="app-row">
                          <div className="app-info">
                            <FiCode className="app-icon" />
                            <div>
                              <strong>{app.name}</strong>
                              <div className="app-meta">
                                {app.git_repo} • {app.git_branch}
                                {app.schedule_enabled && app.schedule_cron && (
                                  <span className="schedule-badge"> • Custom Schedule</span>
                                )}
                                {!app.schedule_enabled && server.schedule_enabled && (
                                  <span className="schedule-badge inherited"> • Using Server Schedule</span>
                                )}
                                {app.disabled && <span className="disabled-badge"> • Disabled</span>}
                                {/* Last backup badge for this app */}
                                <LastBackupBadge targetType="app" targetId={app._id} />
                              </div>
                            </div>
                          </div>

                          <div className="app-actions">
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={e => openHistory(e, 'app', app)}
                              title="Backup History"
                            >
                              <FiClock size={13} /> History
                            </button>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleBackupApp(app)}
                              disabled={app.disabled}
                            >
                              <FiDatabase /> Backup
                            </button>
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => handleDeploy('app', app)}
                              disabled={app.disabled}
                            >
                              Deploy
                            </button>
                            <button className="btn btn-sm btn-secondary" onClick={() => handleSchedule('app', app)}>
                              <FiClock />
                            </button>
                            <button className="btn btn-sm" onClick={() => handleEditApp(app)}>
                              <FiEdit />
                            </button>
                            <button className="btn btn-sm btn-danger" onClick={() => handleDeleteApp(app._id, server._id)}>
                              <FiTrash2 />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-apps">
                      <p>No applications configured for this server.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* ── Backup History Drawer ── */}
      {historyTarget && (
        <BackupHistoryDrawer
          target={historyTarget.target}
          targetType={historyTarget.targetType}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {showServerModal && (
        <ServerModal server={editingServer} onSave={handleSaveServer} onClose={() => setShowServerModal(false)} />
      )}
      {showAppModal && (
        <AppModal app={editingApp} onSave={handleSaveApp} onClose={() => setShowAppModal(false)} />
      )}
      {showScheduleModal && scheduleTarget && (
        <ScheduleModal target={scheduleTarget.target} type={scheduleTarget.type} onSave={handleSaveSchedule} onClose={() => setShowScheduleModal(false)} />
      )}
      {showDeployModal && deployTarget && (
        <DeployModal target={deployTarget.target} type={deployTarget.type} onDeploy={handleDeploySubmit} onClose={() => setShowDeployModal(false)} />
      )}
    </div>
  );
}

export default ServersPage;
